import React, { useMemo } from 'react';
import Chart, { cssVar } from '../card/chart';
import type {
  C1BenchmarkResponse,
  C1BreakdownResponse,
  C1CarbonEmissionResponse,
  C1FloorElectricityResponse,
  C1HvacElectricityResponse,
  C1Response,
  C1TotalEnergyResponse,
} from './c1Response';
import styles from './C1AnswerCard.module.scss';

const numberOrNull = (value: unknown) => {
  const number = Number(value);
  return value === null || value === undefined || !Number.isFinite(number) ? null : number;
};

const formatNumber = (value: unknown, digits = 2) => {
  const number = numberOrNull(value);
  return number === null ? '-' : number.toFixed(digits);
};

const formatPercent = (value: unknown, digits = 2) => `${formatNumber(value, digits)}`;

const sum = (values: unknown[]) => values.reduce<number>((total, value) => total + (numberOrNull(value) ?? 0), 0);

const Highlight = ({ children }: { children: React.ReactNode }) => <span className={styles.highlight}>{children}</span>;

const Stat = ({
  label,
  value,
  unit,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  unit?: string;
  tone?: 'danger' | 'success';
}) => (
  <div className={styles.stat}>
    <div className={styles.statLabel}>{label}</div>
    <div className={`${styles.statValue} ${tone ? styles[tone] : ''}`}>
      {value}
      {unit && <span className={styles.unit}>{unit}</span>}
    </div>
  </div>
);
const MainStat = ({
  label,
  category,
  value,
  unit,
  tone,
}: {
  category: string;
  label: string;
  value: React.ReactNode;
  unit?: string;
  tone?: 'danger' | 'success';
}) => (
  <div className={styles.stat}>
    <div className={styles.statLabel}>{label}</div>
    <div className={styles.statMain}>
      <div className={styles.statValue}>{category}</div>
      <div className={`${styles.statValue} ${tone ? styles[tone] : ''}`}>
        {value}
        {unit && <span className={styles.unit}>{unit}</span>}
      </div>
    </div>
  </div>
);

const FloorStat = ({
  label,
  item,
  tone,
}: {
  label: string;
  item?: { floor?: string | null; electricity?: number | null } | null;
  tone: 'danger' | 'success';
}) => (
  <div className={styles.statWithAside}>
    <Stat label={label} value={formatNumber(item?.electricity)} unit="kWh" tone={tone} />
    <div className={styles.floorName}>
      <span>楼层</span>
      <strong>{item?.floor || '-'}</strong>
    </div>
  </div>
);

const ChartPanel = ({ option }: { option: Record<string, unknown> }) => (
  <div className={styles.chartPanel}>
    <Chart option={option} />
  </div>
);

const buildPieOption = (
  data: Array<{ name: string; value: number; percentage?: number | null; color?: string }>,
  unit: string,
) => ({
  tooltip: { trigger: 'item' },
  unit,
  series: [
    {
      type: 'pie',
      data: data.map(item => ({
        name: item.name,
        value: item.value,
        percentage: item.percentage,
      })),
    },
  ],
});

const buildFloorBarOption = (ranking: Array<{ floor?: string | null; electricity?: number | null }>) => ({
  tooltip: {
    trigger: 'axis',
    formatter: (params: any) => {
      console.log(params);
      return `${params[0].name}<br/>${params[0].marker} ${params[0].value}kWh`;
    },
  },
  grid: { left: 20, right: 24, top: 40, bottom: 12 },
  xAxis: {
    type: 'category',
    data: ranking.map(item => item.floor || '-'),
    axisLabel: { rotate: 42, interval: 0 },
  },
  yAxis: { type: 'value', name: 'kWh' },
  series: [
    {
      type: 'bar',
      data: ranking.map((item, index) => ({
        value: numberOrNull(item.electricity) ?? 0,
        itemStyle: { color: index < 3 ? '#dd512e' : '#2cc492' },
      })),
      unit: 'kWh',
    },
  ],
});

const Q1Card = ({ data }: { data: C1TotalEnergyResponse }) => {
  const electricity = data.energyBreakdown?.electricity;
  const gas = data.energyBreakdown?.gas;
  const option = useMemo(
    () =>
      buildPieOption(
        [
          { name: '电力', value: numberOrNull(electricity?.value) ?? 0 },
          { name: '天然气', value: numberOrNull(gas?.value) ?? 0 },
        ],
        'kgce',
      ),
    [electricity?.value, gas?.value],
  );

  return (
    <>
      <h3 className={styles.title}>上个月总能耗</h3>
      <p className={styles.summary}>
        整楼上个月总能耗为 <Highlight>{formatNumber(data.totalEnergy?.value)}</Highlight> kgce
      </p>
      <div className={styles.statGrid}>
        <Stat label="总用电" value={formatNumber(electricity?.rawValue)} unit="kWh" />
        <Stat label="总用气" value={formatNumber(gas?.rawValue)} unit="m³" />
        <Stat label="总用水" value={formatNumber(data.waterUsage)} unit="m³" />
        <Stat
          label="总能耗（TCE）"
          value={
            numberOrNull(data.totalEnergy?.value) === null
              ? '-'
              : formatNumber((numberOrNull(data.totalEnergy?.value) as number) / 1000)
          }
          unit="tce"
        />
      </div>
      <ChartPanel option={option} />
    </>
  );
};

const Q2Card = ({ data }: { data: C1CarbonEmissionResponse }) => {
  const comparison = data.baselineComparison;
  const actualCarbon = comparison?.actualCarbon ?? data.totalCarbon?.value;
  const gridFactor = data.carbonBreakdown?.gridElectricity?.factor;
  const gasFactor = data.carbonBreakdown?.gas?.factor;
  const option = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => `${params[0].name}<br/>${params[0].marker} ${params[0].value}kgCO₂`,
      },
      grid: { left: 20, right: 24, top: 40, bottom: 12 },
      xAxis: { type: 'category', data: ['改造前', data.month || '上月'] },
      yAxis: { type: 'value', name: 'kgCO₂' },
      series: [
        {
          type: 'bar',
          label: {
            show: true,
            position: 'top',
            color: cssVar('--color-text'),
            textBorderWidth: 0,
            formatter: (params: any) => `${params.value}kgCO₂`,
          },
          data: [
            numberOrNull(comparison?.baselineCarbon),
            { value: numberOrNull(actualCarbon), itemStyle: { color: '#28BDDC' } },
          ],
          unit: 'kgCO₂',
        },
      ],
    }),
    [actualCarbon, comparison?.baselineCarbon, data.month],
  );

  return (
    <>
      <h3 className={styles.title}>碳排放及减碳成效</h3>
      <p className={styles.summary}>
        按国网电碳排因子<Highlight>{formatNumber(gridFactor, 4)}</Highlight>kgCO₂/kWh、燃气碳排因子
        <Highlight>{formatNumber(gasFactor, 3)}</Highlight>kgCO₂/m³计算，上月碳排为
        <Highlight>{formatNumber(data.totalCarbon?.value)}</Highlight>kgCO₂
      </p>
      <div className={styles.statGrid}>
        <Stat label="上月碳排" value={formatNumber(data.totalCarbon?.value)} unit="kgCO₂" />
        <Stat label="改造前基线" value={formatNumber(comparison?.baselineCarbon)} unit="kgCO₂" />
        <Stat label="降低量" value={formatNumber(comparison?.reduction)} unit="kgCO₂" />
        <Stat label="降低率" value={comparison ? formatPercent(comparison.reductionRate) : '-'} />
      </div>
      <ChartPanel option={option} />
    </>
  );
};

const Q3Card = ({ data }: { data: C1HvacElectricityResponse }) => {
  const ranking = data.floorRanking || [];
  const option = useMemo(() => buildFloorBarOption(ranking), [ranking]);

  return (
    <>
      <h3 className={styles.title}>各楼层空调用电分析</h3>
      <p className={styles.summary}>
        主楼15个楼层，裙楼4个楼层空调分项电表汇总后，空调用电合计
        <Highlight>{formatNumber(data.totalHvacElectricity?.value)}</Highlight> kWh；
        <Highlight>{data.highestFloor?.floor || '-'}</Highlight>用电最高
      </p>
      <div className={styles.statGrid}>
        <Stat label="空调总用电" value={formatNumber(data.totalHvacElectricity?.value)} unit="kWh" />
        <Stat
          label="占全楼"
          value={numberOrNull(data.rate) === null ? '-' : formatPercent((numberOrNull(data.rate) as number) * 100)}
          unit="%"
        />
        <FloorStat label="最高" item={data.highestFloor} tone="danger" />
        <FloorStat label="最低" item={data.lowestFloor} tone="success" />
      </div>
      <ChartPanel option={option} />
    </>
  );
};

const Q4Card = ({ data }: { data: C1FloorElectricityResponse }) => {
  const ranking = data.floorRanking || [];
  const total = sum(ranking.map(item => item.electricity));
  const option = useMemo(() => buildFloorBarOption(ranking), [ranking]);

  return (
    <>
      <h3 className={styles.title}>各楼层总用电分布</h3>
      <p className={styles.summary}>
        按楼层总电表统计，上个月全楼总用电<Highlight>{formatNumber(total)}</Highlight> kWh；
        {data.highestFloor?.floor || '-'}为最高楼层
      </p>
      <div className={styles.statGrid}>
        <Stat label="全楼总用电" value={formatNumber(total)} unit="kWh" />
        <Stat label="楼层均值" value={formatNumber(data.averageElectricity)} unit="kWh" />
        <FloorStat label="最高" item={data.highestFloor} tone="danger" />
        <FloorStat label="最低" item={data.lowestFloor} tone="success" />
      </div>
      <ChartPanel option={option} />
    </>
  );
};

const Q5Card = ({ data }: { data: C1BenchmarkResponse }) => {
  const energy = data.benchmarkTable?.energy;
  const value = numberOrNull(data.unitAreaEnergy?.value) ?? 0;
  const guideValue = numberOrNull(energy?.guideValue) ?? 0;
  const baselineValue = numberOrNull(energy?.baselineValue) ?? 0;
  const constraintValue = numberOrNull(energy?.constraintValue) ?? 0;
  const max = Math.max(constraintValue * 1.25, value, 1);
  const option = useMemo(() => {
    const ratio = (threshold: number) => Math.min(Math.max(threshold / max, 0), 1);
    const guideRatio = ratio(guideValue);
    const baselineRatio = ratio(baselineValue);
    const constraintRatio = ratio(constraintValue);
    const textColor = cssVar('--color-text-secondary');
    const thresholdLabels = [
      { label: '0', ratio: 0 },
      { label: formatNumber(guideValue), ratio: guideRatio },
      { label: formatNumber(baselineValue), ratio: baselineRatio },
      { label: formatNumber(constraintValue), ratio: constraintRatio },
    ];

    return {
      tooltip: { show: false },
      series: [
        {
          type: 'custom',
          coordinateSystem: 'none',
          silent: true,
          z: 100,
          data: [0],
          renderItem: (_params: unknown, api: { getWidth: () => number; getHeight: () => number }) => {
            const width = api.getWidth();
            const height = api.getHeight();
            const centerX = width * 0.5;
            const centerY = height * 0.74;
            const labelRadius = Math.min(width, height) * 0.5 + 20;

            return {
              type: 'group',
              children: thresholdLabels.map(item => {
                const angle = Math.PI * (1 - item.ratio);
                return {
                  type: 'text',
                  x: centerX + Math.cos(angle) * labelRadius,
                  y: centerY - Math.sin(angle) * labelRadius,
                  style: {
                    text: item.label,
                    fill: textColor,
                    fontSize: 12,
                    fontWeight: 500,
                    textAlign: 'center',
                    textVerticalAlign: 'middle',
                  },
                };
              }),
            };
          },
        },
        {
          type: 'gauge',
          center: ['50%', '72%'],
          radius: '100%',
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max,
          z: 2,
          silent: true,
          axisLine: {
            lineStyle: {
              width: 8,
              color: [
                [guideRatio, '#2CC492'],
                [baselineRatio, '#286FDC'],
                [constraintRatio, '#FFB41C'],
                [1, '#DD512E'],
              ],
            },
          },
          pointer: { show: false },
          anchor: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
        },
        {
          type: 'gauge',
          center: ['50%', '72%'],
          radius: '78%',
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max,
          z: 3,
          silent: true,
          axisLine: {
            lineStyle: { width: 20, color: [[1, 'rgba(40,111,220,0.10)']] },
          },
          pointer: { show: false },
          anchor: { show: false },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          detail: { show: false },
        },
        {
          type: 'gauge',
          center: ['50%', '72%'],
          // Chart 高度固定为 240px；72% 使 6px progress 位于 20px 轨道的径向中心。
          radius: '72%',
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max,
          z: 4,
          progress: {
            show: true,
            width: 6,
            roundCap: false,
            itemStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  {
                    offset: 0,
                    color: '#51DBFF', // 0% 处的颜色
                  },
                  {
                    offset: 1,
                    color: '#51A1FF', // 100% 处的颜色
                  },
                ],
                global: false, // 缺省为 false
              },
            },
          },
          axisLine: {
            lineStyle: { width: 6, color: [[1, 'transparent']] },
          },
          pointer: {
            show: true,
            length: '140%',
            width: 2,
            itemStyle: { color: '#FFFFFF' },
          },
          anchor: {
            show: true,
            showAbove: true,
            size: 12,
            itemStyle: {
              color: cssVar('--color-neutral-2'),
              borderColor: '#FFFFFF',
              borderWidth: 3,
            },
          },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          title: { show: false },
          detail: {
            show: true,
            formatter: (currentValue: number) => `${formatNumber(currentValue)} kgce/m²`,
            color: textColor,
            fontSize: 12,
            offsetCenter: [0, '26%'],
          },
          data: [{ value }],
        },
      ],
    };
  }, [baselineValue, constraintValue, guideValue, max, value]);

  return (
    <>
      <h3 className={styles.title}>对标江苏省定额标准</h3>
      <p className={styles.summary}>
        本楼建筑面积 <Highlight>{formatNumber(data.area, 0)}</Highlight> m²，单位面积能耗为
        <Highlight>{formatNumber(data.unitAreaEnergy?.value)}</Highlight> kgce/m²；单位面积碳排约为
        <Highlight>{formatNumber(data.unitAreaCarbon?.value)}</Highlight>kgCO₂/m²
      </p>
      <div className={styles.statGrid}>
        <Stat label="单位面积能耗" value={formatNumber(data.unitAreaEnergy?.value)} unit="kgce/m²" />
        <Stat label="约束值" value={formatNumber(energy?.constraintValue)} unit="kgce/m²" />
        <Stat label="基准值" value={formatNumber(energy?.baselineValue)} unit="kgce/m²" />
        <Stat label="引导值" value={formatNumber(energy?.guideValue)} unit="kgce/m²" />
      </div>
      <ChartPanel option={option} />
    </>
  );
};

const Q6Card = ({ data }: { data: C1BreakdownResponse }) => {
  const breakdown = data.carbonBreakdown || [];
  const option = useMemo(
    () =>
      buildPieOption(
        breakdown.map(item => ({
          name: item.category || '-',
          value: numberOrNull(item.carbon) ?? 0,
          percentage: item.percentage,
        })),
        'kgCO₂',
      ),
    [breakdown],
  );

  return (
    <>
      <h3 className={styles.title}>主要能耗与碳排来源</h3>
      <p className={styles.summary}>
        本楼<Highlight>{data.mainEnergySource?.category || '-'}</Highlight>是主要能耗来源；
        <Highlight>{data.mainCarbonSource?.category || '-'}</Highlight>在碳排结构中的占比相对更高。
      </p>
      <div className={styles.statGrid}>
        <MainStat
          label="主要能耗"
          category={data.mainEnergySource?.category || '-'}
          value={data.mainEnergySource ? `${formatNumber(data.mainEnergySource.value)}` : '-'}
          unit="kgce"
        />
        <MainStat
          label="主要碳排"
          category={data.mainCarbonSource?.category || '-'}
          value={data.mainCarbonSource ? `${formatNumber(data.mainCarbonSource.value)}` : '-'}
          unit="kgce"
        />
        <Stat label="主要能耗占比" value={formatPercent(data.mainEnergySource?.percentage)} unit="%" />
        <Stat label="主要碳排占比" value={formatPercent(data.mainCarbonSource?.percentage)} unit="%" />
      </div>
      <ChartPanel option={option} />
    </>
  );
};

const C1AnswerCard = ({ data }: { data: C1Response }) => {
  let content: React.ReactNode;

  switch (data.type) {
    case 'Q1':
      content = <Q1Card data={data} />;
      break;
    case 'Q2':
      content = <Q2Card data={data} />;
      break;
    case 'Q3':
      content = <Q3Card data={data} />;
      break;
    case 'Q4':
      content = <Q4Card data={data} />;
      break;
    case 'Q5':
      content = <Q5Card data={data} />;
      break;
    case 'Q6':
      content = <Q6Card data={data} />;
      break;
  }

  return <section className={styles.answer}>{content}</section>;
};

export default React.memo(C1AnswerCard);
